import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_COOKIE_PREFERENCES,
  getStoredCookieConsent,
  hasCookieConsentAnswer,
  isInternalAdminRoute,
  normalizeCookiePreferences,
  saveCookieConsent,
} from '../utils/cookieConsent';
import { setAnalyticsConsentMode, trackPageView } from '../services/analyticsService';
import reportWebVitals from '../reportWebVitals';

const CookieConsentContext = createContext(null);

function applyTrackingConsent(preferences, { bypass = false } = {}) {
  if (bypass) {
    setAnalyticsConsentMode('admin_bypass');
    reportWebVitals();
    return;
  }

  if (preferences.analytics) {
    setAnalyticsConsentMode('granted');
    reportWebVitals();
    if (typeof window !== 'undefined') {
      trackPageView(window.location.pathname + window.location.search);
    }
    return;
  }

  setAnalyticsConsentMode('denied');
}

export function CookieConsentProvider({ children }) {
  const location = useLocation();
  const isInternalRoute = isInternalAdminRoute(location.pathname);

  const [preferences, setPreferences] = useState(DEFAULT_COOKIE_PREFERENCES);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    if (isInternalRoute) {
      applyTrackingConsent(null, { bypass: true });
      setShowBanner(false);
      setShowPreferences(false);
      return;
    }

    const stored = getStoredCookieConsent();
    if (stored?.preferences) {
      setPreferences(stored.preferences);
      setHasAnswered(true);
      setShowBanner(false);
      applyTrackingConsent(stored.preferences);
      return;
    }

    setPreferences(DEFAULT_COOKIE_PREFERENCES);
    setHasAnswered(false);
    setShowBanner(true);
    applyTrackingConsent(DEFAULT_COOKIE_PREFERENCES);
  }, [isInternalRoute, location.pathname]);

  const persistPreferences = useCallback((nextPreferences) => {
    const normalized = normalizeCookiePreferences(nextPreferences);
    saveCookieConsent(normalized);
    setPreferences(normalized);
    setHasAnswered(true);
    setShowBanner(false);
    setShowPreferences(false);
    applyTrackingConsent(normalized);
    return normalized;
  }, []);

  const acceptAll = useCallback(() => {
    persistPreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  }, [persistPreferences]);

  const rejectNonEssential = useCallback(() => {
    persistPreferences(DEFAULT_COOKIE_PREFERENCES);
  }, [persistPreferences]);

  const savePreferences = useCallback(
    (nextPreferences) => persistPreferences(nextPreferences),
    [persistPreferences]
  );

  const openPreferences = useCallback(() => {
    if (isInternalRoute) return;
    setShowPreferences(true);
    setShowBanner(false);
  }, [isInternalRoute]);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
    if (hasCookieConsentAnswer()) {
      setShowBanner(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      isInternalRoute,
      preferences,
      hasAnswered,
      showBanner: !isInternalRoute && showBanner,
      showPreferences: !isInternalRoute && showPreferences,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openPreferences,
      closePreferences,
      allowsAnalytics: isInternalRoute || preferences.analytics,
      allowsMarketing: isInternalRoute || preferences.marketing,
    }),
    [
      isInternalRoute,
      preferences,
      hasAnswered,
      showBanner,
      showPreferences,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openPreferences,
      closePreferences,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent deve ser usado dentro de CookieConsentProvider');
  }
  return context;
}
